import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGlobalLoading } from '../context/GlobalLoadingContext';
import { useUISettings } from '../context/UISettingsContext';
import LandingPage from '../components/LandingPage';
import AuthModal from '../components/AuthModal';

export default function Home() {
  const { token, profile, authReady } = useAuth(); 
  const navigate = useNavigate();
  const location = useLocation();
  const { startLoading, isLoading } = useGlobalLoading();
  const { resolvedTheme, t } = useUISettings();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [tempName, setTempName] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  const [redirectingToAssessment, setRedirectingToAssessment] = useState(false);
  const [sessionNotice, setSessionNotice] = useState<{ title: string; text: string; tone: 'warning' | 'error' } | null>(null);
  const didTriggerRedirect = useRef(false);

  const isWaitingForAuth = !authReady || redirectingToAssessment;

  useEffect(() => {
    const reason = new URLSearchParams(location.search).get('reason');
    if (reason === 'permission-denied') {
      return;
    }
    if (reason === 'company-name-required') {
      // Keep user on login/home so they can re-enter company name instead of auto-redirecting.
      return;
    }
    const fromPath =
      typeof location.state === 'object' && location.state && 'from' in location.state
        ? ((location.state as { from?: { pathname?: string } }).from?.pathname || '')
        : '';
    const fromAdmin = fromPath.startsWith('/admin');
    if (fromAdmin && authReady && token && profile && !profile.isAdmin) {
      navigate('/forbidden', { replace: true });
      return;
    }
    if (!authReady || !token || !profile || didTriggerRedirect.current) {
      return;
    }

    const pendingCompanyName = localStorage.getItem('pending_company_name') || '';
    const pendingCompanyEmail = localStorage.getItem('pending_company_email') || profile?.email || '';
    const resolvedCompanyName =  pendingCompanyName;

    didTriggerRedirect.current = true;
    setRedirectingToAssessment(true);
    if (!isLoading) {
      startLoading({ source: 'home-auth-redirect' });
    }
    navigate('/assessment', {
      replace: true,
      state: { companyName: resolvedCompanyName, companyEmail: pendingCompanyEmail, keepLoading: true, startFreshAssessment: true },
    });
  }, [authReady, token, profile, navigate, startLoading, isLoading, location.search, location.state]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reason = params.get('reason');
    const isLoginRoute = location.pathname === '/login';

    if (isLoginRoute) {
      const pendingCompanyName = localStorage.getItem('pending_company_name') || '';
      setTempName(pendingCompanyName);
      setTempEmail('');
      setShowAuthModal(true);
    }

    if (reason === 'session-expired') {
      setSessionNotice({
        title: t('home.noticeSessionTitle'),
        text: t('home.sessionExpiredMessage'),
        tone: 'warning',
      });
    } else if (reason === 'permission-denied') {
      setSessionNotice({
        title: t('home.noticePermissionTitle'),
        text: t('home.permissionDeniedMessage'),
        tone: 'error',
      });
    } else if (reason === 'company-name-required') {
      setSessionNotice({
        title: t('home.noticeCompanyRequiredTitle'),
        text: t('home.companyRequiredMessage'),
        tone: 'warning',
      });
    } else {
      setSessionNotice(null);
    }
  }, [location.pathname, location.search, t]);

  const handleStart = (companyName: string) => {
    setTempName(companyName);
    localStorage.setItem('pending_company_name', companyName);
    setTempEmail('');

    if (token && profile) {
      setRedirectingToAssessment(true);
      if (!isLoading) {
        startLoading({ source: 'home-start-click' });
      }
      navigate('/assessment', {
        state: { companyName, companyEmail: profile?.email || '', keepLoading: true, startFreshAssessment: true },
      });
    } else {
      setShowAuthModal(true);
    }
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
    setTempEmail('');
    localStorage.removeItem('pending_company_email');
  };

  if (isWaitingForAuth) {
    return null;
  }

  return (
    <>
      {sessionNotice && (
        <div
          data-testid={sessionNotice.tone === 'error' ? 'permission-denied-banner' : 'session-notice-banner'}
          className="fixed left-1/2 top-6 z-[70] w-[min(720px,92vw)] -translate-x-1/2 overflow-hidden rounded-2xl px-5 py-4 text-center text-sm font-semibold"
          style={
            resolvedTheme === 'dark'
              ? {
                  border:
                    sessionNotice.tone === 'error'
                      ? '1px solid rgba(248, 113, 113, 0.35)'
                      : '1px solid rgba(252, 211, 77, 0.32)',
                  background:
                    sessionNotice.tone === 'error'
                      ? 'linear-gradient(120deg, rgba(40,10,16,0.95), rgba(30,41,59,0.94))'
                      : 'linear-gradient(120deg, rgba(12,18,32,0.95), rgba(30,41,59,0.94))',
                  color: sessionNotice.tone === 'error' ? '#fecaca' : '#fef3c7',
                  boxShadow:
                    sessionNotice.tone === 'error'
                      ? '0 20px 60px -24px rgba(239, 68, 68, 0.55)'
                      : '0 20px 60px -24px rgba(245, 158, 11, 0.65)',
                }
              : {
                  border:
                    sessionNotice.tone === 'error'
                      ? '1px solid rgba(239, 68, 68, 0.2)'
                      : '1px solid rgba(30, 64, 175, 0.18)',
                  background:
                    sessionNotice.tone === 'error'
                      ? 'linear-gradient(120deg, rgba(255,241,242,0.99), rgba(254,226,226,0.97))'
                      : 'linear-gradient(120deg, rgba(248,250,252,0.99), rgba(239,246,255,0.97))',
                  color: '#0f172a',
                  boxShadow:
                    sessionNotice.tone === 'error'
                      ? '0 24px 70px -30px rgba(239, 68, 68, 0.26)'
                      : '0 24px 70px -30px rgba(30, 64, 175, 0.24)',
                }
          }
        >
          <span
            className="mb-1 block text-[10px] font-black uppercase tracking-[0.26em]"
            style={{
              color:
                sessionNotice.tone === 'error'
                  ? (resolvedTheme === 'dark' ? 'rgba(248, 113, 113, 0.95)' : '#b91c1c')
                  : (resolvedTheme === 'dark' ? 'rgba(252, 211, 77, 0.9)' : '#1d4ed8'),
            }}
          >
            {sessionNotice.title}
          </span>
          <span className="text-[13px] font-semibold leading-relaxed">
            {sessionNotice.text}
          </span>
        </div>
      )}

      <LandingPage onStart={handleStart} />

     {showAuthModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
    <AuthModal
      companyName={tempName}
      companyEmail={tempEmail}
      onClose={handleCloseAuthModal}
      onSuccess={() => {
                setShowAuthModal(false);
                if (!isLoading) {
                  startLoading({ source: 'home-auth-success' });
                }
                navigate('/assessment', {
                  state: { companyName: tempName, companyEmail: tempEmail, keepLoading: true, startFreshAssessment: true },
                });
            }}
    />
  </div>
)}
    </>
  );
}
